/**
 * @file きき分けあそび (epic_24) の運用値 (閾値) の版管理。
 * @why: 進段・治療の判定値をコードに散らさず 1 か所に集める (epic_24 §5.5 の一覧と 1:1)。
 *   版番号つきで保持するのは、値を変えても過去ログのリプレイ結果が変わらないようにするため
 *   (各セッションの detail.cfg に「そのとき使った版」が残り、リプレイはその版の値で再生する。
 *   epic_24 §6.4)。値を変えるときは既存の版を書き換えず、新しい版を足して
 *   KIKIWAKE_CURRENT_CFG を進める。
 * @failure: 既存版の書き換えはリプレイの決定性 (AC-7) を壊す。テストが版 1 の値を固定している。
 */

export interface KikiwakeConfig {
  /** 追加のめやす: 最新和音の追加から待つ日数。原典どおり 2 週間 (R6/R7、s.70/s.81)。 */
  addWaitDays: number;
  /** 追加のめやす: 最後のミスからのミスなし日数。原典どおり 2 週間 (R6)。 */
  missFreeDays: number;
  /** 追加のめやす: 直近 missFreeDays 日のうち必要な実施日数 (運用値。原典は「毎日」R11)。 */
  minPracticeDays: number;
  /** その日を「実施日」と数える日合計回答数 (運用値)。 */
  practiceDayMinTrials: number;
  /** これ未満の反応時間 (再生完了から ms) は「にせもののミス」としてノーカウント (運用値、R19)。 */
  fastTapMs: number;
  /** 治療の発火: 直近 careWindowPracticeDays 実施日でのペア相互誤答数 (運用値、R17 は型のみ)。 */
  careTriggerCount: number;
  /** 治療判定の窓 (実施日数、運用値)。 */
  careWindowPracticeDays: number;
  /** 「同程度にまちがいあう」と見なす回数差の上限 (運用値)。 */
  careEqualMaxDiff: number;
  /** 治療の復帰: 隔離和音のミスなし実施日数 (運用値)。 */
  careRecoverCleanDays: number;
  /** 出題数 = clamp(questionsPerChord × 出題対象和音数, minQuestions, maxQuestions)。
   *  上限 30 は原典の 1 回 30 問前後 (R21、s.48/81/115)。 */
  questionsPerChord: number;
  minQuestions: number;
  maxQuestions: number;
  /** 最新和音の出題割合 (R20 の機械化、運用値): 追加当日 → 7 日 → 均等になる日。 */
  newChordShareDay0: number;
  newChordShareDay7: number;
  newChordEvenDay: number;
  /** 追加した日の導入で続けてきかせる回数。原典どおり 3 回 (R20、s.79)。 */
  introPlays: number;
  /** かため弾き (治療中、混ぜる練習の最後) の回数 (運用値。s.123 は疲れと集中を見て決める)。 */
  katamePlays: number;
  /** もう一度きくの上限回数/問 (運用値。原典に再生し直しの記述なし)。 */
  maxReplays: number;
  /** 基準ピッチ (中央ラ)。原典どおり 442Hz (R13、s.69)。 */
  pitchA: number;
}

const KIKIWAKE_CFG_V1: KikiwakeConfig = {
  addWaitDays: 14,
  missFreeDays: 14,
  minPracticeDays: 10,
  practiceDayMinTrials: 5,
  fastTapMs: 400,
  careTriggerCount: 3,
  careWindowPracticeDays: 3,
  careEqualMaxDiff: 1,
  careRecoverCleanDays: 2,
  questionsPerChord: 3,
  minQuestions: 5,
  maxQuestions: 30,
  newChordShareDay0: 0.1,
  newChordShareDay7: 0.2,
  newChordEvenDay: 14,
  introPlays: 3,
  katamePlays: 4,
  maxReplays: 2,
  pitchA: 442,
};

export const KIKIWAKE_CONFIGS: Readonly<Record<number, KikiwakeConfig>> = {
  1: KIKIWAKE_CFG_V1,
};

export const KIKIWAKE_CURRENT_CFG = 1;

/** 最新版の運用値 (KIKIWAKE_CONFIGS[KIKIWAKE_CURRENT_CFG] と同じ実体)。 */
export const KIKIWAKE_CURRENT_CONFIG: KikiwakeConfig = KIKIWAKE_CFG_V1;

/** 未知の版 (将来の版のログを古いコードで読んだ場合) は最新版で代用する。 */
export function kikiwakeConfig(version: number): KikiwakeConfig {
  return KIKIWAKE_CONFIGS[version] ?? KIKIWAKE_CURRENT_CONFIG;
}
